import React from 'react'
import { useTheme } from '@/hooks/useTheme'
import { FiltrosReporte } from '@/services/reportService'
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns'
import { es } from 'date-fns/locale'

interface ReportFiltersProps {
  filtros: FiltrosReporte
  onFiltrosChange: (filtros: FiltrosReporte) => void
  tipos?: { id: string; nombre: string }[]
  loadingTipos?: boolean
}

type PeriodoOption = 'mes_actual' | 'trimestre' | 'semestre' | 'anio' | 'personalizado'

const ReportFilters: React.FC<ReportFiltersProps> = ({
  filtros,
  onFiltrosChange,
  tipos = [],
  loadingTipos = false
}) => {
  const { colors } = useTheme()
  const [periodo, setPeriodo] = React.useState<PeriodoOption>('mes_actual')

  // Aplicar período preestablecido
  const handlePeriodoChange = (newPeriodo: PeriodoOption) => {
    setPeriodo(newPeriodo)

    let inicio: Date
    let fin: Date

    const hoy = new Date()

    switch (newPeriodo) {
      case 'mes_actual':
        inicio = startOfMonth(hoy)
        fin = endOfMonth(hoy)
        break
      case 'trimestre':
        inicio = subMonths(hoy, 3)
        fin = hoy
        break
      case 'semestre':
        inicio = subMonths(hoy, 6)
        fin = hoy
        break
      case 'anio':
        inicio = subMonths(hoy, 12)
        fin = hoy
        break
      case 'personalizado':
        // Sin cambios
        return
      default:
        inicio = startOfMonth(hoy)
        fin = endOfMonth(hoy)
    }

    onFiltrosChange({
      ...filtros,
      fecha_inicio: format(inicio, 'yyyy-MM-dd'),
      fecha_fin: format(fin, 'yyyy-MM-dd')
    })
  }

  const handleFechaInicio = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltrosChange({
      ...filtros,
      fecha_inicio: e.target.value
    })
  }

  const handleFechaFin = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltrosChange({
      ...filtros,
      fecha_fin: e.target.value
    })
  }

  const handleTipoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltrosChange({
      ...filtros,
      tipo_id: e.target.value || undefined
    })
  }

  const handleEstadoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFiltrosChange({
      ...filtros,
      estado: e.target.value || undefined
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
      <div className="space-y-4">
        {/* Período preestablecido */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: colors.primaryDark }}>
            Período
          </label>
          <div className="flex gap-2 flex-wrap">
            {(['mes_actual', 'trimestre', 'semestre', 'anio', 'personalizado'] as PeriodoOption[]).map(
              (opt) => (
                <button
                  key={opt}
                  onClick={() => handlePeriodoChange(opt)}
                  className="px-4 py-2 rounded-lg font-medium transition-all"
                  style={{
                    backgroundColor: periodo === opt ? colors.primary : '#e5e7eb',
                    color: periodo === opt ? 'white' : colors.primaryDark
                  }}
                >
                  {opt === 'mes_actual'
                    ? 'Este mes'
                    : opt === 'trimestre'
                      ? 'Trimestre'
                      : opt === 'semestre'
                        ? 'Semestre'
                        : opt === 'anio'
                          ? 'Año'
                          : 'Personalizado'}
                </button>
              )
            )}
          </div>
        </div>

        {/* Rango de fechas personalizado */}
        {periodo === 'personalizado' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: colors.primaryDark }}>
                Fecha inicio
              </label>
              <input
                type="date"
                value={filtros.fecha_inicio || ''}
                onChange={handleFechaInicio}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: colors.primaryDark }}>
                Fecha fin
              </label>
              <input
                type="date"
                value={filtros.fecha_fin || ''}
                onChange={handleFechaFin}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
              />
            </div>
          </div>
        )}

        {/* Tipo de espacio */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: colors.primaryDark }}>
            Tipo de espacio
          </label>
          <select
            value={filtros.tipo_id || ''}
            onChange={handleTipoChange}
            disabled={loadingTipos}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">{loadingTipos ? 'Cargando tipos...' : 'Todos'}</option>
            {tipos.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.nombre}
              </option>
            ))}
          </select>
        </div>

        {/* Estado */}
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: colors.primaryDark }}>
            Estado
          </label>
          <select
            value={filtros.estado || ''}
            onChange={handleEstadoChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
          >
            <option value="">Todos</option>
            <option value="confirmada">Confirmadas</option>
            <option value="cancelada">Canceladas</option>
            <option value="pendiente">Pendientes</option>
          </select>
        </div>
      </div>
    </div>
  )
}

export default ReportFilters
